// First Mile GRN-section drill-down — the list behind a clicked escalation / exclusion figure,
// optionally narrowed to one warehouse (the table's row-level drill-down passes `dock`).
// Hits /first-mile-escalations/rows/; the response `count` equals the clicked tally.
//
// Two row kinds share the table: `grn` rows (the exclusions) carry the GRN's cargo weight — the
// SAME dimensioning the logistics board shows per shipment — plus every reason it's excluded;
// `escalation` rows carry the raised procurement issue instead.
import { Drawer, Pagination, Table, Tag, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import React, { useEffect, useState } from "react";
import {
  EscalationDrilldownKey,
  FirstMileEscalationRow,
  FirstMileExclusionKey,
  listFirstMileEscalationRows,
} from "../control_tower.api";
import { ESCALATION_DRILLDOWN_LABEL, EXCLUSION_LABEL, FIRST_MILE_DOCK_LABEL } from "../first_mile_meta";
import { FirstMileWarehouseKey } from "../types";

const EXCLUSION_COLOR: Record<FirstMileExclusionKey, string> = {
  po_after_6pm: "orange",
  heavy_po: "geekblue",
  large_tile: "purple",
};

export interface EscalationDrilldownRequest {
  bucket: EscalationDrilldownKey;
  dock?: FirstMileWarehouseKey;
}

interface Props {
  request: EscalationDrilldownRequest | null; // null = closed
  dispatchFrom: string;
  dispatchTo: string;
  assignedTo?: string; // same owner filter the clicked figure was scoped by, so counts agree
  onClose: () => void;
}

const dash = <span style={{ color: "#bbb" }}>—</span>;

const fmtDate = (v?: string | null) =>
  v ? new Date(v).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const fmtDateTime = (v?: string | null) =>
  v
    ? new Date(v).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
    : "—";

const columns: ColumnsType<FirstMileEscalationRow> = [
  { title: "PO Number", dataIndex: "po_number", key: "po_number", render: (v) => v ?? dash },
  { title: "Lead ID", dataIndex: "lead_id", key: "lead_id", render: (v) => v ?? dash },
  { title: "Vendor", dataIndex: "vendor", key: "vendor", render: (v) => v ?? dash },
  {
    title: "Warehouse",
    dataIndex: "dock",
    key: "dock",
    render: (v: FirstMileWarehouseKey | null) => (v ? FIRST_MILE_DOCK_LABEL[v] ?? v : dash),
  },
  {
    // A GRN is dated by its dispatch date; an escalation by when it was raised.
    title: "When",
    key: "when",
    render: (_, r) =>
      r.kind === "escalation" ? fmtDateTime(r.raised_at) : fmtDate(r.dispatch_date),
  },
  {
    title: "Weight",
    dataIndex: "weight_kg",
    key: "weight_kg",
    render: (v: number | undefined, r) =>
      r.kind === "escalation" || v === undefined || v === null ? dash : `${v} kg`,
  },
  {
    title: "GRN",
    key: "marked",
    render: (_, r) =>
      r.kind === "escalation" ? (
        dash
      ) : r.marked ? (
        <Tag color="green">Marked</Tag>
      ) : (
        <Tag color="gold">Awaiting</Tag>
      ),
  },
  {
    title: "Reason",
    key: "reason",
    render: (_, r) =>
      r.kind === "escalation" ? (
        <Tooltip title={r.remarks || undefined} overlayStyle={{ maxWidth: 420 }}>
          <Tag color="volcano" style={{ cursor: r.remarks ? "help" : "default" }}>
            {r.issue_type || "Escalation"}
          </Tag>
        </Tooltip>
      ) : r.reasons && r.reasons.length ? (
        <span style={{ display: "inline-flex", flexWrap: "wrap", gap: 4 }}>
          {r.reasons.map((k) => (
            <Tag key={k} color={EXCLUSION_COLOR[k]}>
              {EXCLUSION_LABEL[k]}
            </Tag>
          ))}
        </span>
      ) : (
        dash
      ),
  },
];

const titleFor = (req: EscalationDrilldownRequest) => {
  const base = ESCALATION_DRILLDOWN_LABEL[req.bucket] ?? req.bucket;
  return req.dock ? `${FIRST_MILE_DOCK_LABEL[req.dock]} · ${base}` : base;
};

const FirstMileEscalationDrawer: React.FC<Props> = ({ request, dispatchFrom, dispatchTo, assignedTo, onClose }) => {
  const [rows, setRows] = useState<FirstMileEscalationRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    setPage(1);
  }, [request]);

  useEffect(() => {
    if (!request) return;
    let cancelled = false;
    setLoading(true);
    listFirstMileEscalationRows({
      bucket: request.bucket,
      dock: request.dock,
      dispatch_from: dispatchFrom,
      dispatch_to: dispatchTo,
      assigned_to: assignedTo,
      page,
      page_size: pageSize,
    }).then((res) => {
      if (cancelled) return;
      setRows(res?.results ?? []);
      setTotal(res?.count ?? 0);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [request, dispatchFrom, dispatchTo, assignedTo, page, pageSize]);

  return (
    <Drawer
      title={request ? titleFor(request) : ""}
      width={1040}
      open={!!request}
      onClose={onClose}
      destroyOnClose
    >
      <Table
        rowKey={(r, i) => `${r.kind}-${r.id}-${i}`}
        size="small"
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={false}
        scroll={{ x: 980 }}
      />
      <div style={{ marginTop: 16, textAlign: "right" }}>
        <Pagination
          current={page}
          pageSize={pageSize}
          total={total}
          showSizeChanger
          pageSizeOptions={["20", "50", "100", "200"]}
          showTotal={(t) => `${t} row${t === 1 ? "" : "s"}`}
          onChange={(p, size) => {
            setPage(p);
            setPageSize(size);
          }}
        />
      </div>
    </Drawer>
  );
};

export default FirstMileEscalationDrawer;
